import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';   // 👈 AJOUTÉ
import {
  ArrowRight, ArrowUp, CheckCircle2, ChevronDown, ChevronRight, Star, User, PlayCircle,
  TrendingUp, Zap, Phone, MessageSquare, CalendarDays, Briefcase, Palette,
  PenLine, Nfc, Contact, Share2, UtensilsCrossed, RefreshCw, MonitorSmartphone,
  ArrowLeftRight, Crown, ShieldCheck, Leaf, CreditCard, Check, Menu, X, Minus, Plus,
} from 'lucide-react';

/* ---------- Design tokens (extraits de la maquette) ---------- */
const C = {
  bg: '#f9f9ff',
  bgAlt: '#eff2ff',
  ink: '#000a1e',
  body: '#3c4148',
  primary: '#006b5f',
  primaryHover: '#005a50',
  chip: '#e6ebff',
  navy: '#101f3f',
  pro: '#000617',
  gold: '#c9a24a',
};

const card = 'bg-white rounded-3xl shadow-[0_4px_24px_rgba(15,23,42,0.06)]';
const lift = 'transition-all duration-300 ease-out hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_18px_44px_rgba(15,23,42,0.13)]';
const press = 'transition-all duration-200 ease-out hover:scale-105 active:scale-95 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#006b5f]/30';
const eyebrow = 'text-[11px] font-bold uppercase tracking-[0.08em] text-[#006b5f]';
const h2 = 'font-display font-semibold tracking-tight text-[#000a1e] text-3xl sm:text-[40px] leading-[1.15]';

/* ---------- Données ---------- */
const steps = [
  { n: '01', icon: Palette, title: 'Choisissez & personnalisez votre support', text: 'Cartes en métal noir brossé, bambou naturel éco-responsable ou PVC recyclé haute résistance. Gravées au laser avec votre logo.', foot: 'Finitions premium artisanales' },
  { n: '02', icon: PenLine, title: 'Configurez votre page en 2 minutes', text: "Éditeur en ligne intuitif, sans coder : coordonnées, réseaux sociaux, liens de rendez-vous Calendly, catalogues PDF ou menus de restaurant.", foot: 'Mise à jour instantanée et illimitée' },
  { n: '03', icon: Nfc, title: 'Approchez et connectez instantanément', text: "Un simple tap sur le smartphone de votre interlocuteur ouvre instantanément votre vitrine digitale. Sans friction, ni application requise.", foot: '100% universel iOS & Android' },
];

const useCases = [
  { n: '01', icon: Contact, tint: 'bg-[#eef1ff]', title: 'Carte de Visite Digitale pour Professionnels & Dirigeants', text: "Marquez les esprits dès la première poignée de main. Vos coordonnées s'enregistrent d'un tap dans le carnet d'adresses du client sans risque d'être égarées.", list: ['Fiche vCard native directement dans iOS & Google Contacts', 'Formulaire de rappel pour capter les coordonnées en retour', 'Connexion instantanée LinkedIn et prise de rendez-vous'], ideal: 'Idéal pour : Avocats, Consultants, Dirigeants' },
  { n: '02', icon: Share2, tint: 'bg-[#d5e8e4]', title: 'Micro-site Link-in-Bio pour Créateurs & Indépendants', text: "Fédérez l'intégralité de vos projets créatifs en un point d'entrée unique et hautement soigné. Remplacez avantageusement les solutions génériques.", list: ['Affichage dynamique de portfolio et vidéos intégrées', 'Boutons personnalisés vers Instagram, YouTube, TikTok, Behance', 'Statistiques précises de clics et conversions par lien'], ideal: 'Idéal pour : Photographes, Designers, Coachs' },
  { n: '03', icon: UtensilsCrossed, tint: 'bg-[#fff1d9]', title: 'Menu Digital Interactif pour Restaurants & Cafés', text: 'Modernisez vos tables avec nos chevalets NFC et QR codes dynamiques. Mettez vos cartes à jour en direct sans jamais réimprimer le moindre papier.', list: ['Mise à jour des prix, plats du jour et ruptures en 10 secondes', 'Filtrage intelligent des allergènes et traduction multilingue', "Redirection d'avis Google Maps pour booster votre notoriété locale"], ideal: 'Idéal pour : Brasseries, Hôtels, Bars à cocktails' },
];

const features = [
  { icon: RefreshCw, title: 'Mise à jour en temps réel', text: "Changez de poste, d'adresse ou de numéro de téléphone depuis votre tableau de bord. Votre carte physique redirige instantanément vers vos données actualisées.", foot: 'Aucune réimpression requise' },
  { icon: MonitorSmartphone, title: 'Zéro application requise', text: "Ni pour vous, ni pour vos interlocuteurs. L'expérience s'exécute nativement via le navigateur web standard de chaque appareil iOS et Android.", foot: '100% fluide et universel' },
  { icon: ArrowLeftRight, title: 'Échange de contact bidirectionnel', text: "Ne vous contentez pas de donner votre carte. Grâce au bouton de contact réciproque, votre interlocuteur vous envoie son numéro et e-mail en retour.", foot: 'Enrichissement de carnet instantané' },
  { pro: true, icon: Crown, title: 'Analytics avancés & Export CRM', text: "Mesurez précisément l'impact de chaque interaction : nombre de scans, localisation géographique, taux d'enregistrement vCard et synchronisation automatique avec HubSpot ou Zapier.", foot: 'Inclus dans la formule Pro' },
  { icon: ShieldCheck, title: 'Puce sécurisée & Verrouillage OTA', text: "En cas de perte ou de vol de votre carte physique, verrouillez-la immédiatement à distance depuis votre espace membre sécurisé pour protéger vos données.", foot: 'Protection conforme RGPD' },
  { icon: Leaf, title: 'Écologique & Zéro Déchet', text: "Une seule carte Tapio remplace en moyenne plus de 2 000 cartes en carton jetées chaque année. Faites un geste concret pour la planète tout en soignant votre image.", foot: 'Matériaux durables et recyclables' },
];

const testimonials = [
  { initials: 'TB', name: 'Thomas B.', role: 'Directeur de clientèle • Cabinet Nova', text: "“L'effet 'waouh' lors de mes rendez-vous clients est immédiat. En approchant la carte en métal de leur téléphone, mon profil s'ouvre en une fraction de seconde. J'ai doublé mes prises de rendez-vous.”" },
  { initials: 'CM', name: 'Camille Moreau', role: 'Fondatrice • Studio Vertigo', text: "“Fini le stock de 500 cartes papier qui deviennent obsolètes dès qu'on ajuste notre numéro ou nos offres. La mise à jour en direct depuis le dashboard Tapio est un bonheur absolu pour notre équipe.”" },
  { initials: 'JL', name: 'Julien Laurent', role: 'Restaurateur • Le Comptoir Parisien', text: "“Nous avons déployé les bornes Tapio sur nos 35 tables de bistrot. Les clients scannent instantanément, les allergènes sont clairs et nous avons collecté plus de 400 nouveaux avis Google en deux mois.”" },
];

const faqs = [
  { q: "Mon interlocuteur a-t-il besoin de l'application Tapio ?", a: "Non ! Aucun téléchargement d'application n'est nécessaire pour vos interlocuteurs. Leur smartphone ouvre instantanément votre page via la puce NFC ou le QR code." },
  { q: 'Quels téléphones sont compatibles ?', a: 'Tous les smartphones récents (iPhone et la quasi-totalité des appareils Android) intègrent nativement la technologie de lecture NFC.' },
  { q: 'Puis-je modifier mes informations après réception de la carte ?', a: 'Absolument. Votre tableau de bord Tapio vous permet de mettre à jour vos liens, photos et coordonnées en temps réel, sans changer de carte physique.' },
  { q: "Comment fonctionne l'abonnement Tapio Pro ?", a: "L'abonnement Pro est optionnel (19€/mois) et débloque les fonctionnalités avancées telles que le nom de domaine personnalisé, les statistiques géolocalisées détaillées et l'export CRM automatique." },
  { q: 'Quel est le délai de livraison ?', a: 'Les commandes sont préparées et gravées dans notre atelier en France sous 24h à 48h, puis expédiées rapidement.' },
];

const logos = [
  <span key="1" className="font-display font-extrabold text-xl">LVMH <span className="text-xs font-medium tracking-widest">DIGITAL</span></span>,
  <span key="2" className="font-display font-extrabold text-xl">⠿ KOTO <span className="text-xs font-medium tracking-widest">STUDIO</span></span>,
  <span key="3" className="font-display font-semibold text-xl tracking-wide">HEC <span className="text-sm text-[#006b5f] tracking-widest">ALUMNI</span></span>,
  <span key="4" className="font-display font-semibold text-lg tracking-wide">⚖ CABINET NOVA</span>,
  <span key="5" className="font-display font-extrabold text-xl">Tech <span className="text-[10px] bg-slate-600 text-white px-1.5 py-0.5 rounded">PARIS</span></span>,
  <span key="6" className="font-display font-medium text-xl"><span className="text-amber-500">❀</span> Maison Sélène</span>,
];

const cardModels = [
  { id: 'bambou', name: 'Bambou naturel', desc: 'Éco-responsable, gravure laser', price: 39.9 },
  { id: 'pvc', name: 'PVC recyclé', desc: 'Haute résistance, impression couleur', price: 44.9 },
  { id: 'metal', name: 'Métal noir brossé', desc: 'Finition premium, gravure laser', price: 69.9 },
];

const fmt = (n) => n.toFixed(2).replace('.', ',') + ' €';
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

/* ---------- Hooks & petits composants ---------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
}

function Modal({ open, onClose, title, subtitle, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-[#000a1e]/60 backdrop-blur-sm animate-fade-in" />
      <div role="dialog" aria-modal="true" aria-label={title} className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-7 sm:p-8 animate-pop">
        <button onClick={onClose} aria-label="Fermer" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#eff2ff] flex items-center justify-center hover:scale-110 hover:rotate-90 active:scale-95 transition-all duration-200">
          <X className="w-4 h-4 text-[#000a1e]" />
        </button>
        <h3 className="font-display font-bold text-2xl text-[#000a1e] pr-10">{title}</h3>
        {subtitle && <p className="text-sm mt-1.5">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, error, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#000a1e]">{label}</span>
      <input
        {...props}
        className={`mt-1.5 w-full px-4 py-3 rounded-xl bg-[#f9f9ff] border text-sm text-[#000a1e] transition-all focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] ${error ? 'border-red-400' : 'border-slate-200'}`}
      />
      {error && <span className="text-[11px] text-red-500 mt-1 block">{error}</span>}
    </label>
  );
}

function SuccessView({ title, text, onClose }) {
  return (
    <div className="text-center py-4 animate-pop">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#dcf5ea] flex items-center justify-center animate-pulse-ring">
        <Check className="w-8 h-8 text-[#006b5f]" />
      </div>
      <h4 className="font-display font-bold text-xl text-[#000a1e] mt-5">{title}</h4>
      <p className="text-sm mt-2 leading-relaxed">{text}</p>
      <button onClick={onClose} className={`${press} mt-6 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-8 py-3 rounded-xl`}>Fermer</button>
    </div>
  );
}

/* ---------- Formulaire de commande ---------- */
function OrderForm({ onClose, onDone }) {
  const [model, setModel] = useState('bambou');
  const [qty, setQty] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [order, setOrder] = useState(null);

  const current = cardModels.find((m) => m.id === model);
  const total = current.price * qty;

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (name.trim().length < 2) err.name = 'Indiquez le nom à graver sur la carte.';
    if (!isEmail(email)) err.email = 'Saisissez une adresse e-mail valide.';
    setErrors(err);
    if (Object.keys(err).length) return;
    const ref = 'TP-' + Math.floor(100000 + Math.random() * 900000);
    setOrder(ref);
    onDone && onDone(ref);
  };

  if (order) {
    return <SuccessView title="Commande confirmée" text={`Merci ${name.trim()} ! Votre commande ${order} (${qty} × ${current.name}) est enregistrée. Un e-mail de confirmation est envoyé à ${email.trim()}. Gravure sous 24h à 48h.`} onClose={onClose} />;
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <div className="space-y-2.5">
        <p className="text-xs font-semibold text-[#000a1e]">Modèle de carte</p>
        {cardModels.map((m) => (
          <button
            type="button"
            key={m.id}
            onClick={() => setModel(m.id)}
            aria-pressed={model === m.id}
            className={`w-full flex items-center justify-between gap-3 text-left px-4 py-3.5 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${model === m.id ? 'border-[#006b5f] bg-[#f0faf8]' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          >
            <span>
              <span className="block font-display font-semibold text-sm text-[#000a1e]">{m.name}</span>
              <span className="block text-[11px] text-slate-500">{m.desc}</span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="font-display font-bold text-sm text-[#000a1e]">{fmt(m.price)}</span>
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${model === m.id ? 'border-[#006b5f] bg-[#006b5f]' : 'border-slate-300'}`}>
                {model === m.id && <Check className="w-3 h-3 text-white" />}
              </span>
            </span>
          </button>
        ))}
      </div>

      <Field label="Nom à graver sur la carte" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Mohammed Souamer" error={errors.name} />
      <Field label="Adresse e-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.com" error={errors.email} />

      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-[#000a1e]">Quantité</span>
        <div className="flex items-center gap-3 bg-[#eff2ff] rounded-xl p-1">
          <button type="button" aria-label="Diminuer" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-lg bg-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform"><Minus className="w-4 h-4" /></button>
          <span key={qty} className="w-6 text-center font-display font-bold text-[#000a1e] animate-pop">{qty}</span>
          <button type="button" aria-label="Augmenter" onClick={() => setQty((q) => Math.min(99, q + 1))} className="w-8 h-8 rounded-lg bg-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div>
          <p className="text-xs text-slate-500">Total • Livraison 48h offerte</p>
          <p key={total} className="font-display font-extrabold text-2xl text-[#000a1e] animate-pop">{fmt(total)}</p>
        </div>
        <button type="submit" className={`${press} bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-7 py-3.5 rounded-xl shadow-lg shadow-[#006b5f]/20`}>
          Valider la commande
        </button>
      </div>
    </form>
  );
}

/* ---------- Formulaire devis flotte ---------- */
function QuoteForm({ onClose, onDone }) {
  const [f, setF] = useState({ name: '', company: '', email: '', count: '10' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (f.name.trim().length < 2) err.name = 'Indiquez votre nom.';
    if (f.company.trim().length < 2) err.company = "Indiquez le nom de l'entreprise.";
    if (!isEmail(f.email)) err.email = 'Saisissez une adresse e-mail valide.';
    if (!(parseInt(f.count, 10) >= 5)) err.count = 'Le devis flotte démarre à 5 cartes.';
    setErrors(err);
    if (Object.keys(err).length) return;
    setSent(true);
    onDone && onDone();
  };

  if (sent) return <SuccessView title="Demande envoyée" text={`Merci ${f.name.trim()}. Notre équipe vous envoie un devis pour ${f.count} cartes à ${f.email.trim()} sous 24h ouvrées.`} onClose={onClose} />;

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <Field label="Votre nom" value={f.name} onChange={set('name')} error={errors.name} />
      <Field label="Entreprise" value={f.company} onChange={set('company')} error={errors.company} />
      <Field label="E-mail professionnel" type="email" value={f.email} onChange={set('email')} error={errors.email} />
      <Field label="Nombre de cartes (5 minimum)" type="number" min="5" value={f.count} onChange={set('count')} error={errors.count} />
      <button type="submit" className={`${press} w-full bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold py-3.5 rounded-xl`}>Demander mon devis</button>
    </form>
  );
}

/* ---------- Connexion ---------- */
function LoginForm({ onDone }) {
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (!isEmail(email)) err.email = 'Saisissez une adresse e-mail valide.';
    if (pwd.length < 6) err.pwd = 'Le mot de passe contient au moins 6 caractères.';
    setErrors(err);
    if (Object.keys(err).length) return;
    onDone(email.trim());
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <Field label="Adresse e-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} />
      <Field label="Mot de passe" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} error={errors.pwd} />
      <button type="submit" className={`${press} w-full bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold py-3.5 rounded-xl`}>Se connecter</button>
    </form>
  );
}

/* ---------- Démo interactive : approcher la carte du téléphone ---------- */
function DemoView({ onSave, onClose }) {
  const [tapped, setTapped] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full h-[340px] flex items-center justify-center overflow-hidden rounded-2xl bg-[#eff2ff]">
        <div className="relative w-[170px] h-[300px] rounded-[30px] border-[7px] border-[#000a1e] bg-white overflow-hidden">
          <div className="mx-auto mt-1.5 w-14 h-3.5 bg-[#000a1e] rounded-full" />
          {!tapped ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 pb-10 text-center px-3">
              <Nfc className="w-10 h-10 text-[#006b5f] animate-pulse" />
              <p className="text-[11px] font-semibold text-[#000a1e]">Approchez la carte du téléphone</p>
            </div>
          ) : (
            <div className="animate-pop">
              <div className="mx-2 mt-2 h-16 rounded-xl bg-gradient-to-br from-[#0b6a5d] to-[#3e8f83]" />
              <div className="-mt-6 mx-auto w-12 h-12 rounded-full bg-white border-4 border-white shadow" />
              <p className="text-center font-display font-bold text-sm text-[#000a1e] mt-1">Mohammed Souamer</p>
              <p className="text-center text-[9px] text-[#3c4148]">Directeur Associé • Nova Advisory</p>
              <button
                onClick={() => { setSaved(true); onSave(); }}
                className={`mx-auto mt-3 block w-[80%] text-[10px] font-semibold rounded-full py-2 transition-all hover:scale-105 active:scale-95 ${saved ? 'bg-[#dcf5ea] text-[#006b5f]' : 'bg-[#006b5f] text-white'}`}
              >
                {saved ? '✓ Contact enregistré' : 'Enregistrer contact'}
              </button>
            </div>
          )}
        </div>
        <div className={`absolute w-[150px] h-[92px] rounded-xl bg-gradient-to-br from-[#161b2e] to-[#0a0e1c] shadow-2xl p-3 text-white transition-all duration-700 ease-out ${tapped ? 'left-[52%] top-[40%] opacity-0 scale-75 rotate-0' : 'left-3 top-6 -rotate-[10deg]'}`}>
          <span className="font-display font-bold tracking-widest text-[10px] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#2dd4bf]" />TAPIO</span>
          <span className="block w-5 h-3.5 rounded bg-gradient-to-br from-[#e6c76a] to-[#b8912f] mt-3" />
        </div>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={() => { setTapped((t) => !t); setSaved(false); }} className={`${press} bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-6 py-3 rounded-xl`}>
          {tapped ? 'Rejouer la démo' : 'Approcher la carte'}
        </button>
        <button onClick={onClose} className={`${press} bg-[#eff2ff] text-[#000a1e] font-display font-semibold px-6 py-3 rounded-xl`}>Fermer</button>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function TapioHomePage() {
  const navigate = useNavigate();                                    // 👈 AJOUTÉ
  const [openFaq, setOpenFaq] = useState(null);
  const [modal, setModal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [email, setEmail] = useState('');
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const [saved, setSaved] = useState(false);
  const [user, setUser] = useState(null);
  const toastTimer = useRef(null);

  const notify = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 3200);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const closeModal = useCallback(() => setModal(null), []);
  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openModal = (m) => { setMenuOpen(false); setModal(m); };

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
      setShowTop(window.scrollY > 600);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const subscribe = (e) => {
    e.preventDefault();
    if (!isEmail(email)) { notify('Saisissez une adresse e-mail valide.'); return; }
    notify('Merci ! Vous êtes inscrit(e) à la newsletter.');
    setEmail('');
  };

  const soon = () => notify('Cette page sera bientôt disponible.');

  const navLinks = [
    ['Produits', 'produits'], ["Cas d'usage", 'cas-usage'], ['Fonctionnalités', 'fonctionnalites'], ['Tarifs', 'tarifs'], ['FAQ', 'faq'],
  ];

  const footerCols = [
    ['Produits', [['Cartes Business NFC', () => openModal('order')], ['Menus QR & NFC', () => scrollTo('cas-usage')], ["Plaques d'accueil", () => openModal('order')], ['Porte-clés connectés', () => openModal('order')]]],
    ['Solutions', [['Restauration & Cafés', () => scrollTo('cas-usage')], ['Indépendants & Créateurs', () => scrollTo('cas-usage')], ['Hôtellerie de Luxe', () => scrollTo('cas-usage')], ['Entreprises & Flottes', () => openModal('quote')]]],
    ['Entreprise', [['Fonctionnalités', () => scrollTo('fonctionnalites')], ['Grille Tarifaire', () => scrollTo('tarifs')], ['Foire aux questions', () => scrollTo('faq')], ['Contact & Démo', () => openModal('demo')]]],
  ];

  return (
    <div className="min-h-screen font-body text-[#3c4148] antialiased overflow-x-hidden" style={{ background: C.bg }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display{font-family:'Plus Jakarta Sans',system-ui,sans-serif}
        .font-body{font-family:'Inter',system-ui,sans-serif}
        html{scroll-behavior:smooth}
        section[id]{scroll-margin-top:84px}

        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes floatSm{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes pop{0%{opacity:0;transform:scale(.92) translateY(8px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes heroIn{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes toastIn{from{opacity:0;transform:translate(-50%,20px) scale(.95)}to{opacity:1;transform:translate(-50%,0) scale(1)}}
        @keyframes pulseRing{0%{box-shadow:0 0 0 0 rgba(0,107,95,.35)}100%{box-shadow:0 0 0 22px rgba(0,107,95,0)}}
        @keyframes shine{0%{transform:translateX(-120%) skewX(-20deg)}100%{transform:translateX(320%) skewX(-20deg)}}
        @keyframes blob{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.15) translate(-20px,-16px)}}

        .animate-float{animation:float 6s ease-in-out infinite}
        .animate-float-slow{animation:float 7.5s ease-in-out infinite reverse}
        .animate-float-sm{animation:floatSm 5s ease-in-out infinite}
        .animate-float-sm-d{animation:floatSm 5.5s ease-in-out .8s infinite}
        .animate-fade-in{animation:fadeIn .25s ease-out both}
        .animate-pop{animation:pop .3s ease-out both}
        .animate-hero{animation:heroIn .8s cubic-bezier(.2,.7,.2,1) both}
        .animate-toast{animation:toastIn .35s ease-out both}
        .animate-pulse-ring{animation:pulseRing 1.6s ease-out infinite}
        .animate-blob{animation:blob 12s ease-in-out infinite}
        .shine::after{content:'';position:absolute;top:0;left:0;height:100%;width:35%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent);transform:translateX(-120%) skewX(-20deg)}
        .shine:hover::after{animation:shine .9s ease-out}

        @media (prefers-reduced-motion: reduce){
          *,*::before,*::after{animation-duration:.01ms !important;animation-iteration-count:1 !important;transition-duration:.01ms !important;scroll-behavior:auto !important}
        }
      `}</style>

      {/* Barre de progression */}
      <div className="fixed top-0 left-0 h-[3px] z-[60] bg-[#006b5f] transition-[width] duration-150" style={{ width: `${progress}%` }} />

      {/* NAVBAR */}
      <header className="sticky top-0 z-50 bg-[#f9f9ff]/90 backdrop-blur border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="font-display text-2xl font-extrabold text-[#000a1e] transition-transform hover:scale-105 active:scale-95">Tapio</a>
          <nav className="hidden lg:flex items-center gap-8 text-[15px] font-medium text-[#1f2937]">
            {navLinks.map(([label, id]) => (
              <a
                key={id}
                href={id === 'produits' ? '/produits' : `#${id}`}                         /* 👈 MODIFIÉ */
                onClick={(e) => {
                  e.preventDefault();
                  if (id === 'produits') {                                                /* 👈 AJOUTÉ */
                    navigate('/produits');
                  } else {
                    scrollTo(id);
                  }
                }}
                className="relative hover:text-[#006b5f] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#006b5f] after:transition-transform after:duration-300 hover:after:scale-x-100"
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => openModal('login')} className="hidden sm:block text-[15px] font-medium text-[#1f2937] hover:text-[#006b5f] hover:scale-105 transition-all">
              {user ? 'Mon espace' : 'Connexion'}
            </button>
            <button onClick={() => openModal('order')} className={`${press} bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-5 sm:px-6 py-3 rounded-xl text-sm leading-tight text-left`}>
              Commander <br className="hidden sm:block" />ma carte
            </button>
            <button onClick={() => openModal('login')} aria-label="Mon compte" className="relative w-9 h-9 rounded-full bg-[#000a1e] text-white flex items-center justify-center transition-all hover:scale-110 active:scale-95">
              <User className="w-4 h-4" />
              {user && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#2dd4bf] border-2 border-[#f9f9ff]" />}
            </button>
            <button onClick={() => setMenuOpen((o) => !o)} aria-label="Menu" aria-expanded={menuOpen} className="lg:hidden w-9 h-9 rounded-full bg-[#e6ebff] flex items-center justify-center transition-all active:scale-90">
              {menuOpen ? <X className="w-4 h-4 text-[#000a1e]" /> : <Menu className="w-4 h-4 text-[#000a1e]" />}
            </button>
          </div>
        </div>
        {/* Menu mobile */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-out ${menuOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <nav className="px-6 pb-5 pt-1 flex flex-col gap-1 font-medium text-[#1f2937]">
            {navLinks.map(([label, id]) => (
              <a
                key={id}
                href={id === 'produits' ? '/produits' : `#${id}`}                         /* 👈 MODIFIÉ */
                onClick={(e) => {
                  e.preventDefault();
                  if (id === 'produits') {                                                /* 👈 AJOUTÉ */
                    setMenuOpen(false);
                    navigate('/produits');
                  } else {
                    scrollTo(id);
                  }
                }}
                className="px-3 py-3 rounded-xl hover:bg-[#e6ebff] active:scale-[0.98] transition-all"
              >
                {label}
              </a>
            ))}
            <button onClick={() => openModal('login')} className="text-left px-3 py-3 rounded-xl hover:bg-[#e6ebff] active:scale-[0.98] transition-all">{user ? 'Mon espace' : 'Connexion'}</button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden pt-16 pb-24">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_40%_45%,rgba(215,225,255,0.55),transparent_55%)] animate-blob" />
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="animate-hero inline-flex items-center gap-1.5 bg-[#e9edff] px-3.5 py-1.5 rounded-full text-xs" style={{ animationDelay: '0ms' }}>
              <span>✨</span>
              <span className="font-semibold text-[#006b5f]">La nouvelle référence du networking sans contact</span>
              <span className="text-slate-500">• Puces NFC NTAG216</span>
            </div>

            <h1 className="animate-hero font-display font-bold text-[#000a1e] tracking-tight text-5xl sm:text-6xl lg:text-[64px] leading-[1.08]" style={{ animationDelay: '120ms' }}>
              Transmettez votre univers professionnel d'un simple geste.
            </h1>

            <p className="animate-hero text-lg leading-relaxed max-w-xl" style={{ animationDelay: '240ms' }}>
              Remplacez vos cartes de visite papier par une carte NFC intelligente et durable. Approchez-la d'un smartphone pour partager instantanément votre profil, vos réseaux ou votre carte de restaurant, sans aucune application requise.
            </p>

            <div className="animate-hero flex flex-col sm:flex-row gap-4" style={{ animationDelay: '360ms' }}>
              <button onClick={() => openModal('order')} className={`${press} shine relative overflow-hidden inline-flex items-center justify-center gap-2 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-8 py-4 rounded-xl shadow-lg shadow-[#006b5f]/20 group`}>
                Commander ma carte Tapio <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <button onClick={() => openModal('demo')} className={`${press} inline-flex items-center justify-center gap-2 bg-white text-[#000a1e] font-display font-semibold px-6 py-4 rounded-xl shadow-[0_2px_10px_rgba(15,23,42,0.06)] hover:bg-slate-50`}>
                <PlayCircle className="w-5 h-5 text-[#006b5f]" /> Voir la démo en direct
              </button>
            </div>

            <div className="animate-hero flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-[#1f2937]" style={{ animationDelay: '480ms' }}>
              {['Compatible iOS & Android', 'Sans abonnement obligatoire', 'Livraison 48h offerte'].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#006b5f]" />{t}</span>
              ))}
            </div>
          </div>

          <div className="animate-hero lg:col-span-5 relative flex justify-center pt-10 pb-10" style={{ animationDelay: '300ms' }}>
            <div className="relative w-[300px]">
              <div className="animate-float">
                <div className="rounded-[44px] border-[9px] border-[#000a1e] bg-white overflow-hidden ml-10 h-[470px] shadow-2xl transition-transform duration-500 hover:scale-[1.03]">
                  <div className="mx-auto mt-2 w-24 h-5 bg-[#000a1e] rounded-full" />
                  <div className="mx-3 mt-2 h-[110px] rounded-2xl bg-gradient-to-br from-[#0b6a5d] to-[#3e8f83] relative">
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/90 text-[10px] font-bold text-[#000a1e] px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3 text-[#006b5f]" /> Vérifié
                    </span>
                  </div>
                  <div className="relative -mt-8 mx-auto w-16 h-16 rounded-full bg-white border-4 border-white shadow" />
                  <div className="text-center px-3 mt-1">
                    <h3 className="font-display font-bold text-lg text-[#000a1e]">Mohammed Souamer</h3>
                    <p className="text-[11px] font-medium text-[#3c4148]">Directeur Associé • Nova Advisory</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Paris & Casablanca</p>
                  </div>
                  <div className="mx-4 mt-4 flex gap-2">
                    <button
                      onClick={() => { setSaved(true); notify('Contact enregistré dans votre carnet d’adresses.'); }}
                      className={`flex-1 text-[11px] font-semibold rounded-full py-2.5 text-center transition-all hover:scale-105 active:scale-95 ${saved ? 'bg-[#dcf5ea] text-[#006b5f]' : 'bg-[#006b5f] text-white'}`}
                    >
                      {saved ? '✓ Enregistré' : 'Enregistrer contact'}
                    </button>
                    <button onClick={() => notify('Appel en cours vers Mohammed Souamer…')} aria-label="Appeler" className="w-9 h-9 rounded-full bg-[#e6ebff] flex items-center justify-center transition-all hover:scale-110 active:scale-90"><Phone className="w-3.5 h-3.5 text-[#000a1e]" /></button>
                    <button onClick={() => notify('Ouverture de la messagerie…')} aria-label="Envoyer un message" className="w-9 h-9 rounded-full bg-[#dcf5ea] flex items-center justify-center transition-all hover:scale-110 active:scale-90"><MessageSquare className="w-3.5 h-3.5 text-[#006b5f]" /></button>
                  </div>
                  <div className="mx-4 mt-3 space-y-2">
                    {[[CalendarDays, 'Réserver un entretien (30 min)', 'Ouverture du calendrier de rendez-vous…'], [Briefcase, 'Pitch Deck & Présentation', 'Téléchargement du Pitch Deck…']].map(([Icon, label, msg]) => (
                      <button key={label} onClick={() => notify(msg)} className="w-full flex items-center gap-2 bg-[#eff2ff] rounded-xl px-3 py-2.5 text-[11px] font-semibold text-[#000a1e] text-left transition-all hover:scale-[1.03] hover:bg-[#e3e9ff] active:scale-95">
                        <Icon className="w-3.5 h-3.5 text-[#006b5f]" /><span className="flex-1">{label}</span><ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-[9px] text-slate-500 mt-5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#006b5f] mr-1 animate-pulse" />Tapio Instant Sync • vCard 4.0
                  </p>
                </div>
              </div>

              <div className="absolute -top-2 -left-14 w-[250px] h-[150px] animate-float-slow">
                <div className="w-full h-full rounded-2xl bg-gradient-to-br from-[#161b2e] to-[#0a0e1c] shadow-2xl p-4 -rotate-[8deg] text-white transition-transform duration-500 hover:-rotate-[3deg] hover:scale-110 cursor-pointer" onClick={() => openModal('demo')}>
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold tracking-widest text-sm flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#2dd4bf] animate-pulse" />TAPIO
                    </span>
                    <span className="w-7 h-5 rounded bg-gradient-to-br from-[#e6c76a] to-[#b8912f]" />
                  </div>
                  <div className="mt-10">
                    <p className="text-[9px] tracking-[0.2em] text-slate-400">MEMBRE EXÉCUTIF</p>
                    <p className="font-display font-semibold text-lg">Mohammed Souamer</p>
                    <p className="text-[8px] tracking-[0.15em] text-slate-500 mt-2">NTAG216 MATTE STEEL</p>
                  </div>
                </div>
              </div>

              <div className="absolute top-[215px] -right-14 animate-float-sm">
                <div className="bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 transition-transform duration-300 hover:scale-110">
                  <span className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-[#000a1e]" /></span>
                  <div>
                    <p className="font-display font-bold text-lg leading-none text-[#000a1e]">+300%</p>
                    <p className="text-[11px] text-[#3c4148] mt-0.5">Engagements vCard</p>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-0 -left-8 animate-float-sm-d">
                <div className="bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 transition-transform duration-300 hover:scale-110">
                  <span className="w-9 h-9 rounded-full bg-[#d5e8e4] flex items-center justify-center"><Zap className="w-4 h-4 text-[#006b5f]" /></span>
                  <div>
                    <p className="font-display font-bold text-lg leading-none text-[#000a1e]">⚡ &lt; 0.2s</p>
                    <p className="text-[11px] text-[#3c4148] mt-0.5">Transmission sans contact</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section className="py-12 bg-white/60">
        <div className="max-w-7xl mx-auto px-6 text-center space-y-8">
          <Reveal><p className="text-xs font-bold uppercase tracking-[0.12em] text-[#1f2937]">Ils développent leur réseau avec Tapio</p></Reveal>
          <div className="flex flex-wrap justify-center items-center gap-x-14 gap-y-6 text-slate-500">
            {logos.map((l, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="transition-all duration-300 hover:scale-110 hover:text-[#000a1e] cursor-default">{l}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3 ÉTAPES */}
      <section id="produits" className="py-24">
        <div className="max-w-7xl mx-auto px-6 space-y-14">
          <Reveal>
            <div className="text-center space-y-4 max-w-2xl mx-auto">
              <span className={eyebrow}>Simplicité absolue</span>
              <h2 className={h2}>Trois étapes pour réinventer vos rencontres professionnelles</h2>
              <p className="text-[15px]">Passez à l'ère du networking intelligent en moins de trois minutes, sans compétences techniques.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 120} className="h-full">
                <div className={`${card} ${lift} group p-8 flex flex-col h-full`}>
                  <div className="w-12 h-12 rounded-xl bg-[#e9edff] flex items-center justify-center font-display font-bold text-[#000a1e] transition-all duration-300 group-hover:bg-[#006b5f] group-hover:text-white group-hover:scale-110 group-hover:-rotate-6">{s.n}</div>
                  <h3 className="font-display font-semibold text-xl text-[#000a1e] mt-6 leading-snug">{s.title}</h3>
                  <p className="text-[15px] leading-relaxed mt-3 flex-1">{s.text}</p>
                  <p className="mt-6 flex items-center gap-2 text-xs font-semibold text-[#006b5f]"><s.icon className="w-4 h-4 transition-transform duration-300 group-hover:scale-125" />{s.foot}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAS D'USAGE */}
      <section id="cas-usage" className="py-24" style={{ background: C.bgAlt }}>
        <div className="max-w-7xl mx-auto px-6 space-y-14">
          <Reveal>
            <div className="text-center space-y-4 max-w-2xl mx-auto">
              <span className={eyebrow}>Polyvalence</span>
              <h2 className={h2}>Conçu pour sublimer tous vos points de contact</h2>
              <p className="text-[15px]">Que vous soyez dirigeant d'entreprise, créateur de contenu ou restaurateur, Tapio s'adapte parfaitement à vos exigences.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {useCases.map((u, i) => (
              <Reveal key={u.n} delay={i * 120}>
                <div className={`${card} ${lift} group p-8`}>
                  <div className={`w-12 h-12 rounded-xl ${u.tint} flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}><u.icon className="w-5 h-5 text-[#000a1e]" /></div>
                  <p className="text-xs font-bold text-[#006b5f] mt-6">Cas d'usage {u.n}</p>
                  <h3 className="font-display font-semibold text-2xl text-[#000a1e] leading-snug mt-2">{u.title}</h3>
                  <p className="text-[15px] leading-relaxed mt-3">{u.text}</p>
                  <ul className="mt-6 space-y-3">
                    {u.list.map((li) => (
                      <li key={li} className="flex items-start gap-3 text-[13px] text-[#000a1e]">
                        <CheckCircle2 className="w-4 h-4 text-[#006b5f] shrink-0 mt-0.5" /><span>{li}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => setModal('order')} className="mt-6 w-full flex items-center justify-between gap-3 bg-[#eff2ff] hover:bg-[#006b5f] hover:text-white rounded-xl px-4 py-3.5 text-[13px] font-medium text-[#000a1e] text-left transition-all duration-200 active:scale-95">
                    <span>{u.ideal}</span><ArrowRight className="w-4 h-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGIE */}
      <section id="fonctionnalites" className="py-24">
        <div className="max-w-7xl mx-auto px-6 space-y-14">
          <Reveal>
            <div className="text-center space-y-4 max-w-2xl mx-auto">
              <span className={eyebrow}>Technologie & Maîtrise</span>
              <h2 className={h2}>Conçu avec l'exigence du matériel d'exception</h2>
              <p className="text-[15px]">Chaque détail de la plateforme logicielle et des puces physiques a été pensé pour offrir une fiabilité sans faille.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 120}>
                {f.pro ? (
                  <div className="group relative overflow-hidden rounded-3xl p-8 min-h-[315px] flex flex-col shadow-[0_18px_40px_rgba(0,6,23,0.25)] transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_24px_54px_rgba(0,6,23,0.4)]" style={{ background: C.pro }}>
                    <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.12),transparent_70%)] transition-transform duration-700 group-hover:scale-150" />
                    <div className="relative flex items-center justify-between">
                      <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"><f.icon className="w-5 h-5 text-[#f5d27a]" /></span>
                      <span className="text-xs font-bold text-[#f5d27a] bg-white/10 px-3 py-1.5 rounded-full">Tapio Pro</span>
                    </div>
                    <h4 className="relative font-display font-semibold text-xl text-white mt-6">{f.title}</h4>
                    <p className="relative text-[15px] leading-relaxed text-slate-400 mt-3 flex-1">{f.text}</p>
                    <p className="relative mt-6 flex items-center justify-between text-xs font-semibold text-[#f5d27a]">{f.foot}<TrendingUp className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></p>
                  </div>
                ) : (
                  <div className={`${card} ${lift} group rounded-2xl p-8 flex flex-col`}>
                    <span className="w-10 h-10 rounded-xl bg-[#e1eeea] flex items-center justify-center transition-all duration-300 group-hover:bg-[#006b5f] group-hover:scale-110 group-hover:rotate-6"><f.icon className="w-5 h-5 text-[#006b5f] transition-colors duration-300 group-hover:text-white" /></span>
                    <h4 className="font-display font-semibold text-xl text-[#000a1e] mt-6">{f.title}</h4>
                    <p className="text-[15px] leading-relaxed mt-3 flex-1">{f.text}</p>
                    <p className="mt-6 text-xs font-semibold text-[#006b5f]">{f.foot}</p>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AVIS */}
      <section className="py-24" style={{ background: C.bgAlt }}>
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="space-y-3">
                <span className={eyebrow}>Avis vérifiés</span>
                <h2 className={h2}>Ils ne se passent plus de Tapio</h2>
              </div>
              <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] px-5 py-3 flex items-center gap-3 self-start md:self-auto transition-transform duration-300 hover:scale-105">
                <div className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-5 h-5 fill-[#00b67a] text-[#00b67a]" />)}</div>
                <div>
                  <p className="font-display font-bold text-[#000a1e] leading-none">4.9 / 5</p>
                  <p className="text-[11px] mt-1">Plus de 1 200 avis sur Trustpilot</p>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 120}>
                <div className={`${card} ${lift} p-8 flex flex-col`}>
                  <div className="flex gap-0.5">{[0, 1, 2, 3, 4].map((k) => <Star key={k} className="w-4 h-4 fill-[#c9a24a] text-[#c9a24a]" />)}</div>
                  <p className="italic text-[15px] leading-relaxed text-[#000a1e] mt-4">{t.text}</p>
                  <div className="flex items-center gap-3 mt-8">
                    <span className="w-10 h-10 rounded-full bg-[#e9edff] flex items-center justify-center text-sm font-bold text-[#000a1e]">{t.initials}</span>
                    <div>
                      <p className="font-display font-semibold text-[#000a1e] leading-tight">{t.name}</p>
                      <p className="text-[11px]">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className={`${card} p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}>
              <div className="space-y-5">
                <span className={eyebrow}>Transparence totale</span>
                <h2 className="font-display font-semibold tracking-tight text-[#000a1e] text-3xl sm:text-4xl leading-tight">Une carte achetée = utilisation illimitée à vie</h2>
                <p className="text-[15px] leading-relaxed">
                  Contrairement à d'autres solutions, Tapio ne vous force à aucun abonnement pour utiliser votre carte au quotidien. Vous commandez votre carte physique une seule fois et profitez de votre profil à vie.
                </p>
                <div className="space-y-3 text-sm font-semibold text-[#000a1e]">
                  <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#006b5f] shrink-0 mt-1" /><span>Formule Gratuite : Profil complet, vCard, liens illimités, zéro abonnement mensuel.</span></div>
                  <div className="flex items-start gap-2"><Crown className="w-4 h-4 text-[#c9a24a] shrink-0 mt-1" /><span>Option Tapio Pro (19€/mois) : Nom de domaine personnalisé, synchronisation CRM, statistiques avancées et formulaire de collecte de leads.</span></div>
                </div>
              </div>
              <div className="rounded-3xl p-8 transition-transform duration-300 hover:scale-[1.03]" style={{ background: C.bgAlt }}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#3c4148]">Carte physique Tapio</p>
                    <p className="font-display font-extrabold text-5xl text-[#000a1e] mt-2">dès 39,90 €</p>
                    <p className="text-[11px] text-slate-500 mt-2">Paiement unique • Livraison sous 48h offerte</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-[#006b5f]" />
                </div>
                <button onClick={() => openModal('order')} className={`${press} shine relative overflow-hidden mt-6 block w-full text-center bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold py-4 rounded-xl`}>
                  Choisir mon modèle de carte
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] px-8 sm:px-16 py-14 shadow-[0_24px_50px_rgba(16,31,63,0.25)]" style={{ background: C.navy }}>
              <div className="absolute -bottom-32 -right-24 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(20,120,110,0.35),transparent_65%)] animate-blob" />
              <div className="relative space-y-6 max-w-3xl">
                <span className="inline-flex items-center gap-2 bg-white/10 text-[#7fe9e0] text-xs font-semibold px-3 py-1.5 rounded-lg">
                  <ShieldCheck className="w-3.5 h-3.5" /> Garantie 30 jours satisfait ou remboursé
                </span>
                <h2 className="font-display font-bold text-white text-4xl sm:text-5xl leading-tight tracking-tight">Prêt à moderniser vos échanges professionnels ?</h2>
                <p className="text-[#8b9bc0]">Rejoignez plus de 15 000 professionnels et entreprises qui partagent leur marque d'un simple geste avec les solutions NFC Tapio.</p>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button onClick={() => openModal('order')} className={`${press} shine relative overflow-hidden flex items-center justify-between gap-6 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-8 py-5 rounded-xl group`}>
                    <span>Commander ma carte immédiatement</span><ArrowRight className="w-4 h-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                  <button onClick={() => openModal('quote')} className={`${press} flex items-center bg-[#26365a] hover:bg-[#2e406a] text-white font-display font-semibold px-8 py-5 rounded-xl`}>
                    Demander un devis flotte entreprise
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 space-y-12">
          <Reveal>
            <div className="text-center space-y-4">
              <span className={eyebrow}>Foire aux questions</span>
              <h2 className="font-display font-semibold tracking-tight text-[#000a1e] text-3xl sm:text-4xl">Questions fréquemment posées</h2>
              <p className="text-[15px]">Tout ce que vous devez savoir avant de passer commande.</p>
            </div>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 70}>
                <div className={`bg-[#f9f9ff] rounded-2xl shadow-[0_1px_4px_rgba(15,23,42,0.05)] transition-all duration-300 hover:shadow-[0_6px_20px_rgba(15,23,42,0.08)] ${openFaq === i ? 'ring-2 ring-[#006b5f]/20' : ''}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left font-display font-semibold text-[#000a1e] hover:text-[#006b5f] transition-colors"
                  >
                    <span>{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-[#006b5f] shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  <div className="grid transition-all duration-300 ease-out" style={{ gridTemplateRows: openFaq === i ? '1fr' : '0fr', opacity: openFaq === i ? 1 : 0 }}>
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 text-[15px] leading-relaxed">{f.a}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: C.bgAlt }} className="pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1.1fr] gap-10">
          <div className="space-y-4">
            <span className="font-display text-xl font-extrabold text-[#000a1e]">Tapio</span>
            <p className="text-[15px] leading-relaxed max-w-sm">
              La plateforme de transition physique-numérique par NFC de référence. Cartes de visite connectées et menus digitaux interactifs conçus pour valoriser votre image de marque.
            </p>
            <p className="flex items-center gap-2 text-xs font-semibold text-[#1f2937]"><Nfc className="w-4 h-4" />Technologie NFC ISO 14443 & QR Code dynamique</p>
          </div>
          {footerCols.map(([title, links]) => (
            <div key={title}>
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#000a1e] mb-4">{title}</h4>
              <ul className="space-y-2.5 text-[15px]">
                {links.map(([label, action]) => (
                  <li key={label}>
                    <button onClick={action} className="text-left hover:text-[#006b5f] hover:translate-x-1 transition-all duration-200">{label}</button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#000a1e] mb-4">Restez informé</h4>
            <p className="text-sm leading-relaxed mb-4">Recevez nos dernières innovations et actualités matérielles.</p>
            <form onSubmit={subscribe} noValidate className="flex items-center bg-white rounded-xl p-1 transition-shadow focus-within:ring-4 focus-within:ring-[#006b5f]/15">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Votre adresse email" aria-label="Adresse email" className="flex-1 min-w-0 px-3 py-2 text-sm bg-transparent focus:outline-none" />
              <button type="submit" className="bg-[#000a1e] text-white text-xs font-bold px-4 py-2.5 rounded-lg hover:bg-[#101f3f] hover:scale-105 active:scale-95 transition-all">S'inscrire</button>
            </form>
            <p className="text-[11px] text-slate-500 mt-2">Zéro spam. Désinscription à tout moment.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-14 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
          <p>© 2025 Tapio Technologies SAS. Tous droits réservés.</p>
          <div className="flex flex-wrap gap-6 font-medium text-[#1f2937]">
            {['Mentions légales', 'Politique de confidentialité', 'CGV & Rétractation', 'Sécurité & RGPD'].map((l) => (
              <button key={l} onClick={soon} className="hover:text-[#006b5f] hover:scale-105 transition-all">{l}</button>
            ))}
          </div>
        </div>
      </footer>

      {/* Retour en haut */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Retour en haut"
        className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-[#006b5f] text-white shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-90 ${showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}`}
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* Toast */}
      {toast && (
        <div role="status" className="animate-toast fixed bottom-6 left-1/2 z-[120] max-w-[90vw] bg-[#000a1e] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#2dd4bf] shrink-0" />{toast}
        </div>
      )}

      {/* MODALES */}
      <Modal open={modal === 'order'} onClose={closeModal} title="Commander ma carte Tapio" subtitle="Paiement unique, livraison 48h offerte, utilisation à vie.">
        <OrderForm onClose={closeModal} onDone={(ref) => notify(`Commande ${ref} enregistrée.`)} />
      </Modal>
      <Modal open={modal === 'quote'} onClose={closeModal} title="Devis flotte entreprise" subtitle="Tarifs dégressifs dès 5 cartes, gravure du logo incluse.">
        <QuoteForm onClose={closeModal} onDone={() => notify('Demande de devis envoyée.')} />
      </Modal>
      <Modal open={modal === 'login'} onClose={closeModal} title={user ? 'Mon espace Tapio' : 'Connexion'} subtitle={user ? undefined : 'Accédez à votre tableau de bord Tapio.'}>
        {user ? (
          <div className="space-y-4">
            <p className="text-sm">Connecté en tant que <strong className="text-[#000a1e]">{user}</strong>.</p>
            <button onClick={() => { setUser(null); closeModal(); notify('Vous êtes déconnecté(e).'); }} className={`${press} w-full bg-[#eff2ff] text-[#000a1e] font-display font-semibold py-3.5 rounded-xl`}>Se déconnecter</button>
          </div>
        ) : (
          <LoginForm onDone={(mail) => { setUser(mail); closeModal(); notify('Connexion réussie. Bienvenue !'); }} />
        )}
      </Modal>
      <Modal open={modal === 'demo'} onClose={closeModal} title="Démo en direct" subtitle="Approchez la carte du téléphone, comme vos interlocuteurs le feront.">
        <DemoView onSave={() => notify('Contact enregistré dans votre carnet d’adresses.')} onClose={closeModal} />
      </Modal>
    </div>
  );
}